/* eslint-disable no-underscore-dangle */

import i18n from '@/i18n';
import { Formulary, Option } from '@/modules/core/domain/interfaces/IFormulary';
import { HttpResponse, IApiHelper } from '@/modules/shared/data/protocols/http';
import {
  BaseApiCreateParams,
  ExportTypeEnum,
} from '@/modules/shared/domain/interfaces';

interface ExporterServiceInstanceParams {
  api: IApiHelper;
  baseUrl: string;
  exportType: ExportTypeEnum;
}

interface NestedFilterParams {
  parentableType: string;
  filterQuestionId: string;
  filterBy: Record<string, string[]>;
}

interface IExporterService {
  getTemplate: (extraParams?: string) => Promise<Formulary>;
  execute: (
    params: BaseApiCreateParams,
    forceExport?: boolean,
  ) => Promise<HttpResponse<any>>;
  getNestedFilterOptions: (params: NestedFilterParams) => Promise<Option[]>;
}

class ExporterServiceUseCase implements IExporterService {
  private _api: IApiHelper;
  private _baseUrl: string;
  private _exportType: ExportTypeEnum;
  private _exporterTemplate: Formulary;
  constructor(params: ExporterServiceInstanceParams) {
    const { api, baseUrl, exportType } = params;
    this._api = api;
    this._baseUrl = baseUrl;
    this._exportType = exportType;
  }

  public async getTemplate(extraParams?: string): Promise<Formulary> {
    if (this._exporterTemplate) return this._exporterTemplate;
    const response = await this._api.get({
      url: `${this._baseUrl}/new?export_type=${this._exportType}${
        extraParams ? `${extraParams}` : ''
      }`,
    });
    this._exporterTemplate = response.body;
    return response.body;
  }

  public async execute(
    params: BaseApiCreateParams,
    forceExport?: boolean,
  ): Promise<HttpResponse<any>> {
    const { options, ...rest } = params;
    const response = await this._api.post({
      url: `${this._baseUrl}`,
      body: {
        export_type: this._exportType,
        ...rest,
        ...(forceExport && { force_export: true }),
        options: {
          language: i18n.language,
          ...options,
        },
      },
    });
    return response;
  }

  public async getNestedFilterOptions({
    parentableType,
    filterQuestionId,
    filterBy,
  }: NestedFilterParams): Promise<Option[]> {
    const filterByQuery = Object.entries(filterBy)
      .flatMap(([key, values]) =>
        (values || []).map(
          (value) => `&filter_by[${key}][]=${encodeURIComponent(value)}`,
        ),
      )
      .join('');

    const response = await this._api.get({
      url: `${this._baseUrl}/nested_filter?parentable_type=${parentableType}&export_type=${this._exportType}&filter_question_id=${filterQuestionId}${filterByQuery}`,
    });
    return response.body;
  }
}

export { ExporterServiceUseCase };
export type { ExporterServiceInstanceParams, IExporterService };
